import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { PhoneIncoming, LogOut, Delete, Minus, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Logo, InstallAppButton } from "@/components/Brand";
import { errMsg, fmt, fmtTime, headerApi } from "@/lib/api";
import { PaymentPicker } from "@/pages/panel/NewOrderDialog";

const KEY = "gs_caller_code";

function Login({ onLogin }) {
  const [code, setCode] = useState("");
  const submit = async () => {
    try { await headerApi("X-Caller-Code", code).get("/caller/me"); localStorage.setItem(KEY, code); onLogin(code); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="min-h-screen bg-[#FF6B0A] text-white flex flex-col items-center justify-center p-6 gap-6" data-testid="caller-login">
      <div className="bg-white rounded-3xl p-2"><Logo className="h-20 w-20" /></div>
      <div className="text-center"><h1 className="font-display text-3xl font-extrabold">Gazi Sepet Caller ID</h1><p className="text-white/80 text-sm mt-1">Panel &gt; Caller ID sayfasındaki bağlantı kodunu girin</p></div>
      <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="BAĞLANTI KODU" className="max-w-xs h-14 text-center text-2xl tracking-[0.25em] font-mono-num bg-white text-[#0F1F45]" data-testid="caller-code-input" />
      <Button className="w-full max-w-xs h-12 rounded-full bg-[#0F1F45] hover:bg-[#0A142F]" onClick={submit} data-testid="caller-login-btn">Bağlan</Button>
      <InstallAppButton testId="caller-install-btn" />
    </div>
  );
}

function OrderForm({ api, customer, onDone }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState({});
  const [f, setF] = useState({ customer_name: customer.name, address: customer.address, note: "", payment_method: "nakit" });
  useEffect(() => { api.get("/caller/products").then((r) => setProducts(r.data)).catch(() => {}); }, [api]);
  const items = products.filter((p) => cart[p.id] > 0);
  const total = items.reduce((s, p) => s + p.price * cart[p.id], 0);
  const ch = (id, d) => setCart((c) => ({ ...c, [id]: Math.max(0, (c[id] || 0) + d) }));
  const submit = async () => {
    if (!items.length) return toast.error("Ürün seçin");
    try {
      const { data } = await api.post("/caller/order", { ...f, customer_phone: customer.phone, source: "telefon", items: items.map((p) => ({ product_id: p.id, qty: cart[p.id] })) });
      toast.success(`Sipariş #${data.no} panele gönderildi`); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-3xl bg-white border p-4 space-y-3" data-testid="caller-order-form">
      <p className="font-display font-bold">Sipariş Oluştur</p>
      <Input placeholder="Müşteri adı" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="caller-order-name" />
      <Textarea placeholder="Adres" rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} data-testid="caller-order-address" />
      <div className="max-h-64 overflow-y-auto divide-y">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-2 py-2 text-sm">
            <span className="flex-1">{p.name} <span className="text-[#FF6B0A] font-semibold">{fmt(p.price)}</span></span>
            <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => ch(p.id, -1)} data-testid={`caller-minus-${p.id}`}><Minus className="h-3.5 w-3.5" /></Button>
            <span className="w-5 text-center font-bold">{cart[p.id] || 0}</span>
            <Button size="icon" className="h-8 w-8 rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={() => ch(p.id, 1)} data-testid={`caller-plus-${p.id}`}><Plus className="h-3.5 w-3.5" /></Button>
          </div>
        ))}
        {!products.length && <p className="text-sm text-slate-500 py-2">Menüde ürün yok.</p>}
      </div>
      <PaymentPicker value={f.payment_method} onChange={(v) => setF({ ...f, payment_method: v })} prefix="caller-payment" />
      <Input placeholder="Not" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="caller-order-note" />
      <Button className="w-full h-12 rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={submit} data-testid="caller-order-submit">Siparişi Gönder • {fmt(total)}</Button>
    </div>
  );
}

function Main({ code, onLogout }) {
  const api = useRef(headerApi("X-Caller-Code", code)).current;
  const [biz, setBiz] = useState("");
  const [num, setNum] = useState("");
  const [customer, setCustomer] = useState(null);
  const [calls, setCalls] = useState([]);
  const [ordering, setOrdering] = useState(false);
  const loadCalls = useCallback(() => api.get("/caller/calls").then((r) => setCalls(r.data)).catch(() => {}), [api]);
  useEffect(() => { api.get("/caller/me").then((r) => setBiz(r.data.business_name)).catch(() => onLogout()); loadCalls(); }, [api, loadCalls, onLogout]);

  const record = async (phone = num) => {
    try {
      await api.post("/caller/call", { phone });
      const { data } = await api.get("/caller/lookup", { params: { phone: phone.replace(/[^\d+]/g, "") } });
      setCustomer(data); setOrdering(false); loadCalls();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "0", "del"];

  return (
    <div className="min-h-screen bg-slate-50 pb-10" data-testid="caller-main">
      <header className="bg-[#0F1F45] text-white px-5 pt-6 pb-5 rounded-b-[2rem] flex items-center gap-3">
        <div className="bg-white rounded-2xl p-1"><Logo className="h-10 w-10" /></div>
        <div className="flex-1"><p className="font-display text-xl font-bold">Caller ID</p><p className="text-xs text-white/60" data-testid="caller-business-name">{biz}</p></div>
        <Button size="icon" variant="ghost" className="text-white" onClick={onLogout} data-testid="caller-logout-btn"><LogOut className="h-5 w-5" /></Button>
      </header>
      <div className="px-4 mt-4 space-y-4 max-w-md mx-auto">
        <InstallAppButton testId="caller-install-btn" tone="light" />
        <div className="rounded-3xl bg-white border p-4">
          <p className="text-xs text-slate-500">Gelen arama numarası</p>
          <Input value={num} onChange={(e) => setNum(e.target.value)} inputMode="tel" placeholder="05XX XXX XX XX" className="mt-1 h-14 text-2xl font-mono-num text-center border-0 bg-slate-50" data-testid="caller-number-input" />
          <div className="grid grid-cols-3 gap-2 mt-3">
            {keys.map((k) => (
              <button key={k} onClick={() => setNum((n) => (k === "del" ? n.slice(0, -1) : n + k))} className="h-12 rounded-2xl bg-slate-100 font-display text-xl font-bold active:bg-[#FF6B0A] active:text-white" data-testid={`caller-key-${k}`}>
                {k === "del" ? <Delete className="mx-auto h-5 w-5" /> : k}
              </button>
            ))}
          </div>
          <Button className="w-full mt-3 h-12 rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] gap-2" onClick={() => record()} data-testid="caller-record-btn"><PhoneIncoming className="h-5 w-5" />Müşteriyi Göster</Button>
        </div>
        {customer && (
          <div className="rounded-3xl bg-[#0F1F45] text-white p-5" data-testid="caller-customer-card">
            <p className="font-mono-num text-xl font-bold">{customer.phone}</p>
            <p className="mt-1 font-semibold" data-testid="caller-customer-name">{customer.name || "Yeni müşteri"}</p>
            {customer.address && <p className="text-sm text-white/70">{customer.address}</p>}
            <p className="text-xs text-[#FFB27A] mt-2">{customer.order_count} sipariş • toplam {fmt(customer.total_spent)}</p>
            {customer.orders.slice(0, 3).map((o) => <p key={o.id} className="text-xs text-white/60 mt-1">#{o.no} • {o.items.map((i) => i.name).join(", ")} • {fmt(o.total)}</p>)}
            <div className="flex gap-2 mt-4">
              <a href={`tel:${customer.phone}`} className="flex-1" data-testid="caller-callback-btn"><Button variant="outline" className="w-full rounded-full bg-transparent text-white border-white/30">Geri Ara</Button></a>
              <Button className="flex-1 rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={() => setOrdering(true)} data-testid="caller-create-order-btn">Sipariş Oluştur</Button>
            </div>
          </div>
        )}
        {customer && ordering && <OrderForm api={api} customer={customer} onDone={() => { setOrdering(false); setCustomer(null); setNum(""); }} />}
        <div className="rounded-3xl bg-white border p-4">
          <p className="font-display font-bold mb-2">Son Aramalar</p>
          {calls.map((c) => (
            <button key={c.id} onClick={() => { setNum(c.phone); record(c.phone); }} className="w-full flex items-center gap-2 py-2 text-sm border-b last:border-0" data-testid={`caller-recent-${c.id}`}>
              <span className="font-mono-num font-semibold">{c.phone}</span><span className="truncate">{c.name}</span><span className="ml-auto text-xs text-slate-500">{fmtTime(c.at)}</span>
            </button>
          ))}
          {!calls.length && <p className="text-sm text-slate-500">Henüz arama yok.</p>}
        </div>
      </div>
    </div>
  );
}

export default function CallerApp() {
  const [code, setCode] = useState(() => localStorage.getItem(KEY));
  useEffect(() => { window.GaziNative?.setCallerCode(code || ""); }, [code]);
  const logout = useCallback(() => { localStorage.removeItem(KEY); setCode(null); }, []);
  return code ? <Main code={code} onLogout={logout} /> : <Login onLogin={setCode} />;
}
